import { api } from './api';

export interface ClassroomInSchool {
  id: number;
  name: string;
  school_id: number;
  admin_id: number | null;
  profesor: string;
  is_admin: boolean;
  is_member: boolean;
  ratingPercent?: number | null;
  ratingDelta?: number | null;
  ratingTrend?: 'up' | 'down' | 'flat' | null;
  ratingTrendWindowDays?: number | null;
}

export interface BackendClassroomStats {
  rating_percent: number | null;
  rating_delta?: number | null;
  rating_trend?: 'up' | 'down' | 'flat' | null;
  rating_trend_window_days?: number | null;
}

export interface MyEnrollRequest {
  classroom_id: number | null;
  classroom_name: string;
  status: 'pending' | 'accepted' | 'rejected' | 'revoked' | string;
  message?: string | null;
}

export interface TeacherInClassroom {
  id: number;
  full_name: string;
  is_admin: boolean;
  email?: string;
  phone?: string | null;
  titles?: string[];
  subjects?: Array<{
    id: number;
    name: string;
    gradebook_config_id?: number | null;
    period_index?: number | null;
  }>;
  subject_names?: string[];
}

export interface StudentSummary {
  id: number;
  full_name: string;
  email: string;
  over16?: boolean;
  is_graduated?: boolean;
  graduated_at?: string | null;
  graduated_school_year?: string | null;
  phone?: string | null;
  address?: string | null;
  parent_name1?: string | null;
  parent_email1?: string | null;
  parent_phone1?: string | null;
  parent_name2?: string | null;
  parent_email2?: string | null;
  parent_phone2?: string | null;
  parent_user_id1?: number | null;
  parent_user_id2?: number | null;
  date_of_birth?: string | null;
}

export interface CreateClassroomResponse {
  id: number;
  name: string;
  school_id: number;
  admin_id: number | null;
}

export interface StudentCreatePayload {
  name: string;
  birth_date: string | null;
  over16: boolean;

  student_email: string | null;
  student_phone: string | null;
  student_address: string | null;

  parent_name1: string | null;
  parent_email1: string | null;
  parent_phone1: string | null;

  parent_name2: string | null;
  parent_email2: string | null;
  parent_phone2: string | null;
}

export async function fetchClassesInMySchool(): Promise<ClassroomInSchool[]> {
  const resp = await api.get('/api/scoli/toate-clasele-din-scoala');
  return resp.data;
}

export async function fetchClassroomStats(classId: number): Promise<BackendClassroomStats> {
  const resp = await api.get(`/api/scoli/classrooms/${classId}/stats`);
  return resp.data;
}

export async function fetchMyEnrollRequests(): Promise<MyEnrollRequest[]> {
  const resp = await api.get('/api/users/my-enroll-requests');
  return resp.data;
}

export async function createClassroom(name: string): Promise<CreateClassroomResponse> {
  const resp = await api.post('/api/scoli/create-classroom', { name });
  return resp.data;
}

export async function joinClassroom(classId: number): Promise<{ message: string; request_id?: number }> {
  const resp = await api.post(`/api/scoli/classrooms/${classId}/join`, {});
  return resp.data;
}

export async function leaveClassroom(classId: number): Promise<{ message: string }> {
  const resp = await api.post(`/api/scoli/classrooms/${classId}/leave`, {});
  return resp.data;
}

export async function renameClassroom(classId: number, newName: string): Promise<void> {
  await api.put(`/api/scoli/classrooms/${classId}`, { new_name: newName });
}

export async function deleteClassroom(classId: number, password: string): Promise<void> {
  await api.delete(`/api/scoli/classrooms/${classId}`, { data: { password } });
}

export async function fetchTeachersInClassroom(classId: number): Promise<TeacherInClassroom[]> {
  const resp = await api.get(`/api/scoli/classrooms/${classId}/teachers`);
  return resp.data;
}

export async function addAdminToClassroom(classId: number, userId: number): Promise<void> {
  await api.post(`/api/scoli/classrooms/${classId}/add-admin`, { user_id: userId });
}

export async function fetchStudentsInClassroom(classId: number, opts?: { include_graduated?: boolean }): Promise<StudentSummary[]> {
  const resp = await api.get(`/api/scoli/clasa/${classId}/elevi`, { params: { include_graduated: opts?.include_graduated ? 1 : 0 } });
  return resp.data;
}

export async function addStudentToClassroom(
  classroomId: number,
  payload: StudentCreatePayload
): Promise<{ student_id: number; generated_password?: string | null; parents_notified?: boolean }> {
  const resp = await api.post(`/api/classrooms/${classroomId}/students`, payload);
  return resp.data;
}

export async function deleteStudentFromClassroom(classroomId: number, studentId: number): Promise<void> {
  await api.delete(`/api/classrooms/${classroomId}/students/${studentId}`);
}
